// Downloads: a project as a zip of its files, and a web build as blitzcc
// --target web -o writes one (blitz/web_export.cpp). That is either one page
// that holds the whole program (writeSingleFileSite), which runs opened in a
// browser or put on any web host, or a zip of a site (writeSite): the runtime's
// page, runtime.js and runtime.wasm, and the program's program.wasm,
// program.json and program.b3dpak. Unzipped on any static host, the site runs
// the program, and very large games load from it with less memory.

import { writeZip } from "./zip.js";
import { versioned } from "./site.js";

export function download(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 60000);
}

// files: [[path, Blob or string], ...]
export async function projectZip(files) {
  const entries = [];
  for (const [path, contents] of files) {
    entries.push([path, typeof contents === "string" ? contents : new Uint8Array(await contents.arrayBuffer())]);
  }
  return writeZip(entries);
}

// program.json, as programJson in blitz/web_export.cpp writes it. A site's
// has versions, of its page, runtime, program and package (siteVersions), and
// start, the files its page downloads before it starts the program (startList
// in compiler/compile.mjs).
export function programJson({ title, launchDirectory, commandLine = "", debug, versions = {}, start = [] }) {
  const description = JSON.stringify({ title, launchDirectory, commandLine, debug, pageVersion: versions.page,
    runtimeVersion: versions.runtime, programVersion: versions.program, packageVersion: versions.package }, null, 2);
  if (!start.length) return `${description}\n`;
  return `${description.slice(0, -2)},\n  "start": [${start.map((file) => JSON.stringify(file)).join(", ")}]\n}\n`;
}

// What startList (compiler/compile.mjs) reads of a package (packAssets'
// B3DPAK1): every file's path, and the files that tell what loads with a
// world, the worlds and their glTF files.
export function startInputs(assets) {
  const bytes = new Uint8Array(assets), view = new DataView(assets);
  const packaged = [], files = {};
  let at = 12;
  for (let count = view.getUint32(8, true); count > 0; --count) {
    const pathLength = view.getUint32(at, true), size = view.getUint32(at + 4, true);
    const path = new TextDecoder().decode(bytes.subarray(at + 8, at + 8 + pathLength));
    packaged.push(path);
    if (/\.(world|gltf)$/i.test(path)) files[path] = bytes.slice(at + 8 + pathLength, at + 8 + pathLength + size);
    at += 8 + pathLength + size;
  }
  return { packaged, files };
}

// One of the web runtime's files. runtime: the URL of the run page's folder,
// which holds the runtime.
async function runtimeFile(runtime, name) {
  const response = await fetch(versioned(new URL(name, runtime)));
  if (!response.ok) throw new Error(`The web runtime's ${name} could not be loaded (${response.status})`);
  return response;
}

// Formats compressed already, which a package stores as they are
// (compressedFormat in blitz/web_export.cpp).
const COMPRESSED = new Set(["png", "jpg", "jpeg", "webp", "ogg", "mp3", "ktx2", "basis", "zip", "gz", "woff2", "mp4",
  "webm", "b3dpak"]);

let crcTable = null;
// The CRC-32 of bytes, continued from crc.
function crc32(bytes, crc = 0) {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let value = 0; value < 256; ++value) {
      let crc = value;
      for (let bit = 0; bit < 8; ++bit) crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
      crcTable[value] = crc >>> 0;
    }
  }
  crc ^= 0xffffffff;
  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 255] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const hexVersion = (crc) => crc.toString(16).padStart(8, "0");

// The versions program.json names, as writeSite in blitz/web_export.cpp
// writes them: the CRC-32 of the page, of runtime.js and then runtime.wasm,
// of program.wasm, and of the package's index (rangedPackage's). The page
// loads the runtime, the program and a whole package by them, since a browser
// may keep a site's files for days on a host that sends no Cache-Control.
export function siteVersions({ page, script, module, wasm, pack }) {
  const index = pack.subarray(12, 12 + new DataView(pack.buffer, pack.byteOffset).getUint32(8, true));
  return { page: hexVersion(crc32(page)), runtime: hexVersion(crc32(module, crc32(script))),
    program: hexVersion(crc32(wasm)), package: hexVersion(crc32(index)) };
}

// program.b3dpak as blitzcc writes a site's (writeAssetPackage in
// blitz/web_export.cpp): B3DPAK2, "B3DPAK2\0", the index's length, the index
// (a file count, then each file's path length, UTF-8 path, 64-bit offset,
// stored size, size, CRC-32 and compression: 0 stored, 1 zlib), then the
// files. Each file is deflated on its own, when that saves a tenth and its
// format is not compressed already, so that the page can fetch the index and
// then each file as a byte range. assets is packAssets' B3DPAK1.
export async function rangedPackage(assets) {
  const bytes = new Uint8Array(assets), view = new DataView(assets);
  const files = [];
  let at = 12;
  for (let count = view.getUint32(8, true); count > 0; --count) {
    const pathLength = view.getUint32(at, true), size = view.getUint32(at + 4, true);
    const path = bytes.subarray(at + 8, at + 8 + pathLength);
    const contents = bytes.subarray(at + 8 + pathLength, at + 8 + pathLength + size);
    at += 8 + pathLength + size;
    const name = new TextDecoder().decode(path);
    let stored = contents, compressed = false;
    if (size >= 64 && !COMPRESSED.has(name.replace(/^.*\./, "").toLowerCase())) {
      const deflated = new Uint8Array(await new Response(new Blob([contents]).stream()
        .pipeThrough(new CompressionStream("deflate"))).arrayBuffer());
      if (deflated.length * 10 < size * 9) {
        stored = deflated;
        compressed = true;
      }
    }
    files.push({ path, contents, stored, compressed });
  }
  const indexLength = 4 + files.reduce((total, file) => total + 4 + file.path.length + 21, 0);
  const total = 12 + indexLength + files.reduce((sum, file) => sum + file.stored.length, 0);
  const out = new Uint8Array(total), outView = new DataView(out.buffer);
  out.set(new TextEncoder().encode("B3DPAK2\0"));
  outView.setUint32(8, indexLength, true);
  outView.setUint32(12, files.length, true);
  let entry = 16, data = 12 + indexLength;
  for (const file of files) {
    outView.setUint32(entry, file.path.length, true);
    out.set(file.path, entry + 4);
    entry += 4 + file.path.length;
    outView.setUint32(entry, data % 4294967296, true);
    outView.setUint32(entry + 4, Math.floor(data / 4294967296), true);
    outView.setUint32(entry + 8, file.stored.length, true);
    outView.setUint32(entry + 12, file.contents.length, true);
    outView.setUint32(entry + 16, crc32(file.contents), true);
    out[entry + 20] = file.compressed ? 1 : 0;
    entry += 21;
    out.set(file.stored, data);
    data += file.stored.length;
  }
  return out;
}

// start: program.json's start list (startList in compiler/compile.mjs).
export async function webBuildZip({ runtime, title, launchDirectory, debug, wasm, assets, start = [] }) {
  const fetchBytes = async (name) => new Uint8Array(await (await runtimeFile(runtime, name)).arrayBuffer());
  const [page, script, module] = await Promise.all(["index.html", "runtime.js", "runtime.wasm"].map(fetchBytes));
  const program = new Uint8Array(wasm), pack = await rangedPackage(assets);
  const versions = siteVersions({ page, script, module, wasm: program, pack });
  // The page holds its own version where it marks the program's place.
  const text = new TextDecoder().decode(page).replace(PROGRAM_MARKER,
    () => `<meta name="b3d-page-version" content="${versions.page}">`);
  return writeZip([
    ["index.html", new TextEncoder().encode(text)],
    ["runtime.js", script],
    ["runtime.wasm", module],
    ["program.wasm", program],
    ["program.json", programJson({ title, launchDirectory, debug, versions, start })],
    ["program.b3dpak", pack],
  ]);
}

// Where the runtime's page (web/shell/index.html) takes the program's
// description, and the program's files.
const PROGRAM_MARKER = "<!-- b3d:program -->";
const DATA_MARKER = "<!-- b3d:data -->";
const DEFAULT_TITLE = "<title>Blitz3D+</title>";
// A megabyte of base64, which decodes without padding.
const BLOCK_BYTES = 3 * 256 * 1024;

const htmlText = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// btoa takes a string of bytes, made here a whole number of 3-byte groups at
// a time, so the pieces join without padding between them.
const base64 = (bytes) => {
  if (typeof bytes.toBase64 === "function") return bytes.toBase64();
  const piece = 3 * 8192;
  let text = "";
  for (let at = 0; at < bytes.length; at += piece) {
    text += btoa(String.fromCharCode.apply(null, bytes.subarray(at, at + piece)));
  }
  return text;
};

// A file's blocks, as the page holds them: its zlib stream split into
// BLOCK_BYTES pieces (the last shorter), each a script element of base64,
// which the page decodes in turn and inflates as one stream. Each block is a
// Blob of its own, so the page's text is not all kept as strings.
async function embeddedBlocks(name, contents) {
  const blocks = [];
  let block = new Uint8Array(BLOCK_BYTES), filled = 0;
  const emit = () => {
    blocks.push(new Blob([`<script type="application/octet-stream" class="b3d-data" data-file="${name}">`,
      base64(block.subarray(0, filled)), "</script>\n"]));
    block = new Uint8Array(BLOCK_BYTES);
    filled = 0;
  };
  const reader = contents.stream().pipeThrough(new CompressionStream("deflate")).getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    for (let at = 0; at < value.length;) {
      const take = Math.min(value.length - at, BLOCK_BYTES - filled);
      block.set(value.subarray(at, at + take), filled);
      filled += take;
      at += take;
      if (filled === BLOCK_BYTES) emit();
    }
  }
  if (filled) emit();
  return blocks;
}

// A single-file web build, as blitzcc -o game.html writes it: the runtime's
// page with the program's description where it marks its place, and
// runtime.js, runtime.wasm, program.wasm and program.b3dpak, in that order,
// where it marks the end. Returns a Blob.
export async function webPage({ runtime, title, launchDirectory, debug, wasm, assets }) {
  const [page, script, module] = await Promise.all([
    runtimeFile(runtime, "index.html").then((response) => response.text()),
    runtimeFile(runtime, "runtime.js").then((response) => response.blob()),
    runtimeFile(runtime, "runtime.wasm").then((response) => response.blob()),
  ]);
  const programAt = page.indexOf(PROGRAM_MARKER), dataAt = page.indexOf(DATA_MARKER);
  if (programAt < 0 || dataAt < programAt) throw new Error("The web runtime's page cannot hold a single-file web build");
  const blocks = [];
  for (const [name, contents] of [["runtime.js", script], ["runtime.wasm", module],
    ["program.wasm", new Blob([wasm])], ["program.b3dpak", new Blob([assets])]]) {
    blocks.push(...await embeddedBlocks(name, contents));
  }
  // Inside the script element, "<" is escaped so the text cannot end it.
  const description = `{"program": ${programJson({ title, launchDirectory, debug }).trim()},\n  "blocks": ${blocks.length}}`
    .replace(/</g, "\\u003c");
  const head = page.slice(0, programAt).replace(DEFAULT_TITLE, () => `<title>${htmlText(title)}</title>`);
  const parts = [head, `<script type="application/json" id="b3d-embedded">${description}</script>`,
    page.slice(programAt + PROGRAM_MARKER.length, dataAt), ...blocks, page.slice(dataAt + DATA_MARKER.length)];
  return new Blob(parts, { type: "text/html" });
}
